import React, { useState } from 'react';
import {
  DocumentDuplicateIcon as Copy,
  ArrowDownTrayIcon as Download,
  EyeIcon as Eye,
  EyeSlashIcon as EyeOff,
  ExclamationTriangleIcon as AlertTriangle,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/Card';
import { Button } from '../UI/Button';
import { Alert, AlertDescription } from '../UI/Alert';

interface BackupCodesProps {
  codes: string[];
  onClose?: () => void;
}

export const BackupCodes: React.FC<BackupCodesProps> = ({ codes, onClose }) => {
  const [showCodes, setShowCodes] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const copyToClipboard = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const copyAllCodes = async () => {
    const allCodes = codes.join('\n');
    try {
      await navigator.clipboard.writeText(allCodes);
      setCopiedIndex(-1); // -1 indica que todos foram copiados
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar todos os códigos:', err);
    }
  };

  const downloadCodes = () => {
    const content = `Códigos de Backup - Autenticação de Dois Fatores\n\nGuarde estes códigos em local seguro. Cada código pode ser usado apenas uma vez.\n\n${codes.join('\n')}\n\nData de geração: ${new Date().toLocaleString()}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backup-codes-2fa-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card className='w-full max-w-md'>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <AlertTriangle className='h-5 w-5 text-amber-600' />
          Códigos de Backup
        </CardTitle>
        <CardDescription>
          Guarde estes códigos em local seguro. Cada um pode ser usado apenas uma vez para acessar sua conta se você
          perder acesso ao seu aplicativo autenticador.
        </CardDescription>
      </CardHeader>

      <CardContent className='space-y-4'>
        <Alert>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>
            <strong>Importante:</strong> Estes códigos não serão mostrados novamente. Salve-os agora!
          </AlertDescription>
        </Alert>

        <div className='flex items-center justify-between'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setShowCodes(!showCodes)}
            className='flex items-center gap-2'
          >
            {showCodes ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
            {showCodes ? 'Ocultar' : 'Mostrar'} Códigos
          </Button>
          <Button variant='outline' size='sm' onClick={downloadCodes} className='flex items-center gap-2'>
            <Download className='h-4 w-4' />
            Baixar
          </Button>
        </div>

        {showCodes && (
          <div className='space-y-2'>
            <div className='grid grid-cols-1 gap-2 max-h-60 overflow-y-auto'>
              {codes.map((code, index) => (
                <div key={index} className='flex items-center justify-between p-3 bg-gray-50 rounded-lg border'>
                  <code className='font-mono text-sm text-gray-800'>{code}</code>
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={() => copyToClipboard(code, index)}
                    className='ml-2 h-8 w-8 p-0'
                  >
                    <Copy className='h-3 w-3' />
                  </Button>
                  {copiedIndex === index && <span className='text-xs text-green-600 ml-2'>Copiado!</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
          <h4 className='font-medium text-blue-900 mb-2'>Como usar os códigos de backup:</h4>
          <ul className='text-sm text-blue-800 space-y-1'>
            <li>• Use apenas se não conseguir acessar seu aplicativo autenticador</li>
            <li>• Cada código funciona apenas uma vez</li>
            <li>• Digite o código no campo de verificação 2FA</li>
            <li>• Gere novos códigos quando restarem poucos</li>
          </ul>
        </div>

        {onClose && (
          <div className='flex justify-end pt-4'>
            <Button onClick={onClose}>Entendi, fechar</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BackupCodes;
